/**
 * Vue Router — 어드민 모든 페이지 auth 필수 (login 제외).
 *
 * 도메인별 페이지는 Sprint 1+ 에서 추가:
 *   /admin/pages          → permissions
 *   /admin/users          → users
 *   /admin/cases          → cases
 *   ...
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { authGuard } from './guards';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { requireAuth: false },
  },
  {
    path: '/',
    component: () => import('@/components/layouts/AdminLayout.vue'),
    // 화면별 접근 판정은 meta.roles 가 아니라 페이지 권한(canShow) 하나로 한다.
    // 예전에는 여기 roles: ['Admin User','Admin Manager'] 가 모든 자식에 상속돼,
    // 권한 데이터로 아무리 열어도 콘솔이 열리지 않았다(라우트마다 예외를 코드에
    // 추가해야 했다). 이제 Access → Permissions 화면에서 켜고 끄면 그대로 반영된다.
    meta: { requireAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'readiness',
        name: 'readiness',
        component: () => import('@/pages/readiness.vue'),
        // Backend allows ADMIN_ROLES + FINANCE_ROLES (case-readiness.routes.ts) —
        // the page is finance-safe (fee shown as paid/unpaid only). Override the
        // Admin-only parent meta so Accounting roles aren't blocked by the guard.
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/customers',
        name: 'relocation-customers',
        component: () => import('@/pages/relocation/customers/index.vue'),
        // 부모 meta 는 Admin User/Manager 전용이라 relocation 역할이 막힌다.
        // 백엔드는 페이지 권한 데이터로 판정하므로 프론트도 같은 집합을 연다.
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/followups',
        name: 'relocation-followups',
        component: () => import('@/pages/relocation/followups/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/bulk-email',
        name: 'relocation-bulk-email',
        component: () => import('@/pages/relocation/bulk-email/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/rate-notices',
        name: 'relocation-rate-notices',
        component: () => import('@/pages/relocation/rate-notices/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/hourly-rates',
        name: 'relocation-hourly-rates',
        component: () => import('@/pages/relocation/hourly-rates/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/payouts',
        name: 'relocation-payouts',
        component: () => import('@/pages/relocation/payouts/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/extra-claims',
        name: 'relocation-extra-claims',
        component: () => import('@/pages/relocation/extra-claims/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/expense-claims',
        name: 'relocation-expense-claims',
        component: () => import('@/pages/relocation/expense-claims/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/topups',
        name: 'relocation-topups',
        component: () => import('@/pages/relocation/topups/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/reviews',
        name: 'relocation-reviews',
        component: () => import('@/pages/relocation/reviews/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/dashboard',
        name: 'relocation-dashboard',
        component: () => import('@/pages/relocation/dashboard/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/lms',
        name: 'relocation-lms',
        component: () => import('@/pages/relocation/lms/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/lms/enrollments',
        name: 'relocation-lms-enrollments',
        component: () => import('@/pages/relocation/lms/enrollments.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/lms/access',
        name: 'relocation-lms-access',
        component: () => import('@/pages/relocation/lms/access.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/lms/progress',
        name: 'relocation-lms-progress',
        component: () => import('@/pages/relocation/lms/progress.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/codes',
        name: 'relocation-codes',
        component: () => import('@/pages/relocation/codes/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/expense-types',
        name: 'relocation-expense-types',
        component: () => import('@/pages/relocation/expense-types/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/checklist-items',
        name: 'relocation-checklist-items',
        component: () => import('@/pages/relocation/checklist-items/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/terms',
        name: 'relocation-terms',
        component: () => import('@/pages/relocation/terms/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/topup-packages',
        name: 'relocation-topup-packages',
        component: () => import('@/pages/relocation/topup-packages/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/dispatch',
        name: 'relocation-dispatch',
        component: () => import('@/pages/relocation/dispatch/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/active',
        name: 'relocation-active',
        component: () => import('@/pages/relocation/active/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/anomalies',
        name: 'relocation-anomalies',
        component: () => import('@/pages/relocation/anomalies/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/schedules',
        name: 'relocation-schedules',
        component: () => import('@/pages/relocation/schedules/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/field-staff',
        name: 'relocation-field-staff',
        component: () => import('@/pages/relocation/field-staff/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/service-areas',
        name: 'relocation-service-areas',
        component: () => import('@/pages/relocation/service-areas/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'relocation/customers/:id',
        name: 'relocation-customer-detail',
        component: () => import('@/pages/relocation/customers/[id].vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'email-templates',
        name: 'email-templates',
        component: () => import('@/pages/email-templates/index.vue'),
      },
      {
        path: 'email-templates/new',
        name: 'email-templates-new',
        component: () => import('@/pages/email-templates/new.vue'),
      },
      {
        path: 'email-templates/:id',
        name: 'email-templates-edit',
        component: () => import('@/pages/email-templates/[id].vue'),
      },
      {
        path: 'contact',
        name: 'contact',
        component: () => import('@/pages/contact/index.vue'),
      },
      {
        path: 'permissions',
        name: 'permissions',
        component: () => import('@/pages/permissions/index.vue'),
      },
      {
        path: 'service-requests',
        name: 'service-requests',
        component: () => import('@/pages/service-requests/index.vue'),
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/pages/users/index.vue'),
      },
      {
        path: 'users/:id',
        name: 'users-detail',
        component: () => import('@/pages/users/[id].vue'),
      },
      {
        path: 'invitations',
        name: 'invitations',
        component: () => import('@/pages/invitations/index.vue'),
      },
      {
        path: 'crm',
        name: 'crm',
        component: () => import('@/pages/crm/index.vue'),
      },
      {
        path: 'audit-log',
        name: 'audit-log',
        component: () => import('@/pages/audit-log/index.vue'),
      },
      {
        path: 'employers',
        name: 'employers',
        component: () => import('@/pages/employers/index.vue'),
      },
      {
        path: 'employers/new',
        name: 'employers-new',
        component: () => import('@/pages/employers/new.vue'),
      },
      {
        path: 'employers/:id',
        name: 'employers-detail',
        component: () => import('@/pages/employers/[id].vue'),
      },
      {
        path: 'crm/contract-requests',
        name: 'crm-contract-requests',
        component: () => import('@/pages/crm/contract-requests/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'legal-advisors',
        name: 'legal-advisors',
        component: () => import('@/pages/legal-advisors/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'agents',
        name: 'agents',
        component: () => import('@/pages/agents/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'agents/new',
        name: 'agents-new',
        component: () => import('@/pages/agents/new.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'agents/:id',
        name: 'agents-detail',
        component: () => import('@/pages/agents/[id].vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'agent-documents',
        name: 'agent-documents',
        component: () => import('@/pages/agent-documents/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'campaigns',
        name: 'campaigns',
        component: () => import('@/pages/campaigns/index.vue'),
      },
      {
        path: 'campaigns/new',
        name: 'campaigns-new',
        component: () => import('@/pages/campaigns/new.vue'),
      },
      {
        path: 'campaigns/calendar',
        name: 'campaigns-calendar',
        component: () => import('@/pages/campaigns/calendar.vue'),
      },
      {
        path: 'campaigns/:id',
        name: 'campaigns-detail',
        component: () => import('@/pages/campaigns/[id].vue'),
      },
      {
        path: 'applicants',
        name: 'applicants',
        component: () => import('@/pages/applicants/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'applicants/new',
        name: 'applicants-new',
        component: () => import('@/pages/applicants/new.vue'),
      },
      {
        path: 'applicants/anomalies',
        name: 'applicants-anomalies',
        component: () => import('@/pages/applicants/anomalies.vue'),
      },
      {
        path: 'applicants/by-campaign',
        name: 'applicants-by-campaign',
        component: () => import('@/pages/applicants/by-campaign.vue'),
        meta: { requireAuth: true },
      },
      {
        // Legacy drill-in URL — redirect to the unified page with campaignId query param.
        path: 'applicants/by-campaign/:campaignId',
        name: 'applicants-by-campaign-detail',
        redirect: (to) => ({
          path: '/applicants/by-campaign',
          query: { campaignId: String(to.params.campaignId) },
        }),
      },
      // Job Apply Request 화면은 Job Applications 로 합쳤다 — 옛 주소는 같은 상태 필터로 넘긴다.
      {
        path: 'applicants/requested',
        redirect: (to) => {
          const status = ({ apply: 'Submitted', cancel: 'CancelRequested', hold: 'Hold' } as Record<string, string>)[
            String(to.query.subTab ?? '')
          ];
          const employerId = to.query.employerId ? String(to.query.employerId) : undefined;
          return {
            path: '/applicants/applications',
            query: { ...(status ? { status } : {}), ...(employerId ? { employerId } : {}) },
          };
        },
      },
      {
        path: 'applicants/current',
        name: 'applicants-current',
        component: () => import('@/pages/applicants/current.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'applicants/applications',
        name: 'applicants-applications',
        component: () => import('@/pages/applicants/applications.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'applicants/:id',
        name: 'applicants-detail',
        component: () => import('@/pages/applicants/[id].vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'cases',
        name: 'cases',
        component: () => import('@/pages/cases/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'watching',
        name: 'watching',
        component: () => import('@/pages/cases/watching.vue'),
      },
      {
        path: 'deadline/lc-to-i140',
        name: 'deadline-lc-to-i140',
        component: () => import('@/pages/cases/deadline.vue'),
        props: { kind: 'lc-to-i140' },
      },
      {
        path: 'deadline/ad-to-eta',
        name: 'deadline-ad-to-eta',
        component: () => import('@/pages/cases/deadline.vue'),
        props: { kind: 'ad-to-eta' },
      },
      // RFE 응답 마감 — 위 둘(180일 규정 마감)과 접두만 같고 별개 화면이다.
      {
        path: 'deadline/rfe',
        name: 'deadline-rfe',
        component: () => import('@/pages/cases/deadline-rfe.vue'),
      },
      {
        path: 'cases/i140',
        name: 'cases-i140',
        component: () => import('@/pages/cases/i140.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'cases/i485',
        name: 'cases-i485',
        component: () => import('@/pages/cases/i485.vue'),
      },
      {
        path: 'cases/ds260',
        name: 'cases-ds260',
        component: () => import('@/pages/cases/ds260.vue'),
      },
      {
        path: 'cases/i824',
        name: 'cases-i824',
        component: () => import('@/pages/cases/i824.vue'),
      },
      {
        path: 'cases/eta',
        name: 'cases-eta',
        component: () => import('@/pages/cases/eta.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'cases/withdraw',
        name: 'cases-withdraw',
        component: () => import('@/pages/cases/withdraw.vue'),
      },
      {
        path: 'cases/new',
        name: 'cases-new',
        component: () => import('@/pages/cases/new.vue'),
      },
      {
        path: 'cases/applicant/:applicantId',
        name: 'cases-applicant-detail',
        component: () => import('@/pages/cases/applicant/[applicantId].vue'),
      },
      {
        path: 'cases/:id',
        name: 'cases-detail',
        component: () => import('@/pages/cases/[id].vue'),
        meta: { requireAuth: true },
      },
      // ── Case Reports ────────────────────────────────────────
      {
        path: 'reports/cases/by-agent',
        name: 'reports-cases-by-agent',
        component: () => import('@/pages/reports/cases/by-agent.vue'),
      },
      {
        path: 'super-reports/withdrawn',
        name: 'super-reports-withdrawn',
        component: () => import('@/pages/super-reports/withdrawn.vue'),
      },
      {
        path: 'super-reports/campaigns',
        name: 'super-reports-campaigns',
        component: () => import('@/pages/super-reports/campaigns.vue'),
      },
      {
        path: 'super-reports/refunds',
        name: 'super-reports-refunds',
        component: () => import('@/pages/super-reports/refunds.vue'),
      },
      {
        path: 'reports/employers',
        name: 'reports-employers',
        component: () => import('@/pages/reports/employers.vue'),
      },
      {
        path: 'reports/cases/by-employer',
        name: 'reports-cases-by-employer',
        component: () => import('@/pages/reports/cases/by-employer.vue'),
      },
      {
        path: 'reports/cases/by-attorney',
        name: 'reports-cases-by-attorney',
        component: () => import('@/pages/reports/cases/by-attorney.vue'),
      },
      {
        path: 'reports/cases/by-status',
        name: 'reports-cases-by-status',
        component: () => import('@/pages/reports/cases/by-status.vue'),
      },
      {
        path: 'reports/cases/monthly',
        name: 'reports-cases-monthly',
        component: () => import('@/pages/reports/cases/monthly.vue'),
      },
      {
        path: 'faqs',
        name: 'faqs',
        component: () => import('@/pages/faqs/index.vue'),
      },
      {
        path: 'faqs/new',
        name: 'faqs-new',
        component: () => import('@/pages/faqs/new.vue'),
      },
      {
        path: 'faqs/suggestions',
        name: 'faqs-suggestions',
        component: () => import('@/pages/faqs/suggestions.vue'),
      },
      {
        path: 'faqs/:id',
        name: 'faqs-detail',
        component: () => import('@/pages/faqs/[id].vue'),
      },
      {
        path: 'chatbot',
        name: 'chatbot',
        component: () => import('@/pages/chatbot/index.vue'),
      },
      {
        path: 'questionnaires/applicant',
        name: 'questionnaires-applicant',
        component: () => import('@/pages/questionnaires/applicant/index.vue'),
      },
      {
        path: 'questionnaires/applicant/:id',
        name: 'questionnaires-applicant-detail',
        component: () => import('@/pages/questionnaires/applicant/[id].vue'),
      },
      {
        path: 'questionnaires/employer',
        name: 'questionnaires-employer',
        component: () => import('@/pages/questionnaires/employer/index.vue'),
      },
      {
        path: 'questionnaires/employer/:id',
        name: 'questionnaires-employer-detail',
        component: () => import('@/pages/questionnaires/employer/[id].vue'),
      },
      {
        path: 'notifications/notices',
        name: 'notifications-notices',
        component: () => import('@/pages/notifications/notices/index.vue'),
      },
      {
        path: 'notifications/notices/new',
        name: 'notifications-notices-new',
        component: () => import('@/pages/notifications/notices/new.vue'),
      },
      {
        path: 'notifications/notices/:id',
        name: 'notifications-notices-detail',
        component: () => import('@/pages/notifications/notices/[id].vue'),
      },
      {
        path: 'notifications/messages',
        name: 'notifications-messages',
        component: () => import('@/pages/notifications/messages/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'notifications/messages/broadcast',
        name: 'notifications-messages-broadcast',
        component: () => import('@/pages/notifications/messages/broadcast.vue'),
      },
      {
        path: 'notifications/messages/compose',
        name: 'notifications-messages-compose',
        component: () => import('@/pages/notifications/messages/compose.vue'),
      },
      {
        path: 'notifications/messages/:id',
        name: 'notifications-messages-detail',
        component: () => import('@/pages/notifications/messages/[id].vue'),
      },
      {
        path: 'notifications/push',
        name: 'notifications-push',
        component: () => import('@/pages/notifications/push/index.vue'),
      },
      {
        path: 'blog',
        name: 'blog',
        component: () => import('@/pages/blog/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'blog/new',
        name: 'blog-new',
        component: () => import('@/pages/blog/new.vue'),
      },
      {
        path: 'blog/categories',
        name: 'blog-categories',
        component: () => import('@/pages/blog/categories.vue'),
      },
      {
        path: 'blog/:id',
        name: 'blog-detail',
        component: () => import('@/pages/blog/[id].vue'),
      },
      // ── Finance (invoices / payments / commissions / promotions / cashflow) ──
      {
        path: 'finance/invoices',
        name: 'finance-invoices',
        component: () => import('@/pages/finance/invoices/index.vue'),
        meta: { requireAuth: true },
      },
      // /finance/invoices/new 라우트 제거 — invoice 는 payments by-user
      // drawer → "Create Invoice" multi-select 만 진입점 (legacy
      // addInvoice.modal.html parity).
      {
        path: 'finance/invoices/:id',
        name: 'finance-invoices-detail',
        component: () => import('@/pages/finance/invoices/[id].vue'),
      },
      // ── Receipts (legacy TB_Receipt parity) ──
      {
        path: 'finance/receipts',
        name: 'finance-receipts',
        component: () => import('@/pages/finance/receipts/index.vue'),
      },
      // /finance/receipts/new 라우트 제거 — receipt 는 invoice detail 의
      // "+ Receipt" 모달만 진입점 (legacy addReceiptCtrl parity).
      {
        path: 'finance/receipts/:id',
        name: 'finance-receipts-detail',
        component: () => import('@/pages/finance/receipts/[id].vue'),
      },
      {
        path: 'finance/payments',
        name: 'finance-payments',
        component: () => import('@/pages/finance/payments/index.vue'),
      },
      {
        path: 'finance/new-contracts',
        name: 'finance-new-contracts',
        component: () => import('@/pages/finance/new-contracts.vue'),
      },
      {
        path: 'finance/payments/upload',
        name: 'finance-payments-upload',
        component: () => import('@/pages/finance/payments/upload.vue'),
      },
      // Note: finance/payments/bulk-upload + finance/payments/deleted were
      // merged into the payments page as z-[60] modals (Bulk Upload + Deleted
      // Users header buttons). The standalone routes were deleted.
      // Gap #6 — per-user fees standalone page.
      {
        path: 'finance/users/:id/fees',
        name: 'finance-user-fees',
        component: () => import('@/pages/finance/users/[id]/fees.vue'),
      },
      {
        path: 'finance/payment-requests',
        name: 'finance-payment-requests',
        component: () => import('@/pages/finance/payment-requests/index.vue'),
      },
      {
        // 재무의 현장인력 지급 확인. relocation 이 요청한 건만 다루므로 회계
        // 역할로 잠근다 — 요청한 쪽이 스스로 승인하면 통제가 없다.
        path: 'finance/field-staff-payouts',
        name: 'finance-field-staff-payouts',
        component: () => import('@/pages/finance/field-staff-payouts/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'finance/withdraw-refund',
        name: 'finance-withdraw-refund',
        component: () => import('@/pages/finance/withdraw-refund/index.vue'),
      },
      {
        path: 'finance/cost-management',
        name: 'finance-cost-management',
        component: () => import('@/pages/finance/cost-management/index.vue'),
      },
      {
        path: 'finance/disbursement-requests',
        name: 'finance-disbursement-requests',
        component: () => import('@/pages/finance/disbursement-requests/index.vue'),
        // 대납 요청은 officer(요청) ↔ finance(승인) maker/checker 구조다. 라우트가
        // 레이아웃의 Admin 전용 meta 를 상속하고 있어서, 정작 승인 권한을 가진
        // Accounting 역할이 화면에 들어오지도 못했다 (승인 API 는 FINANCE_ROLES).
        // Commission 그룹과 같은 방식으로 Accounting 을 들여보낸다.
        meta: { requireAuth: true },
      },
      {
        path: 'finance/commissions',
        name: 'finance-commissions',
        component: () => import('@/pages/finance/commissions/index.vue'),
        meta: { requireAuth: true },
      },
      // Agent Info — legacy agentCms.html master-detail (agent list + 5 tabs)
      {
        path: 'finance/commissions/agent-info',
        name: 'finance-commissions-agent-info',
        component: () => import('@/pages/finance/commissions/agent-info.vue'),
        meta: { requireAuth: true },
      },
      // Request Commission — tabbed wrapper (Generate / Requests / One-Time Bonus)
      {
        path: 'finance/commissions/request-commission',
        name: 'finance-commissions-request-commission',
        component: () => import('@/pages/finance/commissions/request-commission.vue'),
        meta: { requireAuth: true },
        children: [
          { path: '', redirect: { name: 'finance-commissions-request-commission-generate' } },
          {
            path: 'generate',
            name: 'finance-commissions-request-commission-generate',
            component: () => import('@/pages/finance/commissions/calc.vue'),
          },
        ],
      },
      {
        path: 'finance/commissions/calc',
        name: 'finance-commissions-calc',
        component: () => import('@/pages/finance/commissions/calc.vue'),
      },
      // Static paths must precede the dynamic /:id matcher.
      {
        path: 'finance/commissions/rate-cards',
        name: 'finance-commissions-rate-cards',
        component: () => import('@/pages/finance/commissions/rate-cards.vue'),
      },
      {
        path: 'finance/commissions/types',
        name: 'finance-commissions-types',
        component: () => import('@/pages/finance/commissions/types.vue'),
      },
      {
        path: 'finance/commissions/contracts',
        name: 'finance-commissions-contracts',
        component: () => import('@/pages/finance/commissions/contracts.vue'),
      },
      {
        path: 'finance/commissions/pending-approvals',
        name: 'finance-commissions-pending-approvals',
        component: () => import('@/pages/finance/commissions/pending-approvals/index.vue'),
      },
      {
        path: 'finance/commissions/confirm',
        name: 'finance-commissions-confirm',
        component: () => import('@/pages/finance/commissions/confirm/index.vue'),
        meta: { requireAuth: true },
      },
      {
        path: 'finance/late-fees',
        name: 'finance-late-fees',
        component: () => import('@/pages/finance/late-fees/index.vue'),
      },
      {
        path: 'finance/commissions/history',
        name: 'finance-commissions-history',
        component: () => import('@/pages/finance/commissions/history/index.vue'),
        meta: { requireAuth: true },
      },
      // Commission by Period 는 Commission History 로 합쳤다(2026-11) — 옛 주소는 History 로.
      { path: 'finance/commissions/report', redirect: '/finance/commissions/history' },
      {
        path: 'finance/commissions/by-agent',
        name: 'finance-commissions-by-agent',
        component: () => import('@/pages/finance/commissions/by-agent/index.vue'),
      },
      {
        path: 'finance/commissions/:id',
        name: 'finance-commissions-detail',
        component: () => import('@/pages/finance/commissions/[id].vue'),
      },
      {
        path: 'finance/promotions',
        name: 'finance-promotions',
        component: () => import('@/pages/finance/promotions/index.vue'),
      },
      {
        path: 'finance/promotions/:id',
        name: 'finance-promotions-detail',
        component: () => import('@/pages/finance/promotions/[id].vue'),
      },
      {
        path: 'finance/cashflow',
        name: 'finance-cashflow',
        component: () => import('@/pages/finance/cashflow/index.vue'),
      },
      {
        path: 'finance/cashflow/ledger',
        name: 'finance-cashflow-ledger',
        component: () => import('@/pages/finance/cashflow/ledger.vue'),
      },
      {
        path: 'finance/cashflow/ar-aging',
        name: 'finance-cashflow-ar-aging',
        component: () => import('@/pages/finance/cashflow/ar-aging.vue'),
      },
      // Note: finance/cashflow/day-report was merged into the payments page
      // as a z-[60] modal (Day Report header button). The standalone route
      // was deleted.
      {
        path: 'finance/balance-report',
        name: 'finance-balance-report',
        component: () => import('@/pages/finance/balance-report/index.vue'),
      },
      // Legacy URL preserved for bookmarks — ACH Behalf is now the bottom
      // section of /finance/payment-requests (legacy userReqPmt.html parity).
      {
        path: 'finance/ach-behalf',
        name: 'finance-ach-behalf',
        redirect: { name: 'finance-payment-requests' },
      },
      {
        path: 'finance/prepaid',
        name: 'finance-prepaid',
        component: () => import('@/pages/finance/prepaid/index.vue'),
      },
      // ── Settings (master data — lookup codes, etc.) ──
      {
        path: 'settings/lookup-codes',
        name: 'settings-lookup-codes',
        component: () => import('@/pages/settings/lookup-codes/index.vue'),
      },
      {
        path: 'settings/document-templates',
        name: 'settings-document-templates',
        component: () => import('@/pages/settings/document-templates/index.vue'),
      },
      {
        path: 'settings/help-articles',
        name: 'settings-help-articles',
        component: () => import('@/pages/settings/help-articles/index.vue'),
      },
      {
        path: 'settings/help-articles/new',
        name: 'settings-help-articles-new',
        component: () => import('@/pages/settings/help-articles/new.vue'),
      },
      {
        path: 'settings/mobile-versions',
        name: 'settings-mobile-versions',
        component: () => import('@/pages/settings/mobile-versions/index.vue'),
      },
      {
        path: 'settings/notifications',
        name: 'settings-notifications',
        component: () => import('@/pages/settings/notifications/index.vue'),
      },
      {
        path: 'settings/help-articles/:id',
        name: 'settings-help-articles-detail',
        component: () => import('@/pages/settings/help-articles/[id].vue'),
      },
      // ── System (jobs scheduler) ──
      {
        path: 'jobs',
        name: 'jobs',
        component: () => import('@/pages/jobs/index.vue'),
      },
      {
        path: 'jobs/:name',
        name: 'jobs-detail',
        component: () => import('@/pages/jobs/[name].vue'),
      },
    ],
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/pages/ForbiddenPage.vue'),
    meta: { requireAuth: false },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { requireAuth: false },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(authGuard);

/**
 * 배포 후 남아 있던 탭에서 "메뉴를 눌러도 아무 일이 없는" 문제 처리.
 *
 * 라우트는 전부 `() => import(...)` 로 청크를 나눠 받는다(140곳). 오래 열어둔
 * 탭은 배포 **이전** 파일명(해시)을 들고 있는데, 새 이미지로 교체되면 그 파일은
 * 서버에서 사라진다. 그래서 링크를 누르면 import 가 404 로 실패하고, vue-router
 * 는 그 오류를 삼킨 채 이동을 취소한다 — 화면도 안 바뀌고 에러도 안 뜬다.
 * 새로고침하면 새 index.html 을 받으므로 그때부터 정상 동작한다.
 *
 * 그래서 청크 로드 실패만 골라 전체 새로고침으로 목적지에 간다. 사용자에게는
 * '클릭이 늦게 먹는' 정도로 보이고, 하던 이동은 그대로 완료된다.
 */
const CHUNK_RELOAD_KEY = 'chunkReloadAt';
router.onError((error, to) => {
  const msg = String((error as Error)?.message ?? '');
  const isChunkError =
    /dynamically imported module|Loading chunk|ChunkLoadError|Importing a module script failed|Failed to fetch/i
      .test(msg);
  if (!isChunkError) return;

  // 새로고침해도 계속 실패하면(네트워크 단절 등) 무한 새로고침이 된다.
  // 10초 안에 이미 한 번 했으면 포기하고 오류를 그대로 둔다.
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.assign(to.fullPath);
});
